import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useMuttonStall } from '../../context/muttonStall/MuttonStallContext';
import { useMsPermission } from '../../components/muttonStall/useMsPermission';

const rs = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const toDate = (d) => d.toISOString().slice(0, 10);

const MuttonStallLedgerPage = () => {
  const {
    ledgerAccounts,
    ledgerEntries,
    ledgerCategories,
    fetchLedgerAccounts,
    fetchLedgerEntries,
    fetchLedgerCategories,
    createLedgerAccount,
    createLedgerEntry,
  } = useMuttonStall();
  const { can } = useMsPermission();
  const canManage = can('ms_ledger_manage');

  const [accountForm, setAccountForm] = useState({ accountName: '', openingBalance: '0' });
  const [entryForm, setEntryForm] = useState({
    ledgerAccountId: '',
    category: '',
    subcategory: '',
    amount: '',
    entryType: 'CREDIT',
    description: '',
    transactedDate: toDate(new Date()),
  });
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ accountId: '', category: '', entryType: '' });

  useEffect(() => {
    fetchLedgerAccounts();
    fetchLedgerCategories();
    fetchLedgerEntries();
  }, [fetchLedgerAccounts, fetchLedgerCategories, fetchLedgerEntries]);

  useEffect(() => {
    fetchLedgerEntries({
      accountId: filters.accountId || undefined,
      category: filters.category || undefined,
      entryType: filters.entryType || undefined,
    });
  }, [filters, fetchLedgerEntries]);

  const accountTotals = useMemo(() => {
    const opening = (ledgerAccounts || []).reduce((s, a) => s + Number(a.opening_balance ?? a.openingBalance ?? 0), 0);
    const closing = (ledgerAccounts || []).reduce((s, a) => s + Number(a.closing_balance ?? a.current_balance ?? a.currentBalance ?? 0), 0);
    return { opening, closing };
  }, [ledgerAccounts]);

  const onAccount = async (e) => {
    e.preventDefault();
    if (!accountForm.accountName.trim()) return toast.error('Account name required');
    setSaving(true);
    const result = await createLedgerAccount({
      accountName: accountForm.accountName.trim(),
      openingBalance: Number(accountForm.openingBalance) || 0,
    });
    setSaving(false);
    if (result.success) {
      toast.success('Account created');
      setAccountForm({ accountName: '', openingBalance: '0' });
    } else toast.error(result.error || 'Failed');
  };

  const onEntry = async (e) => {
    e.preventDefault();
    if (!entryForm.ledgerAccountId) return toast.error('Select account');
    if (!entryForm.category) return toast.error('Select category');
    if (!Number(entryForm.amount)) return toast.error('Enter amount');
    setSaving(true);
    const result = await createLedgerEntry({
      ledgerAccountId: entryForm.ledgerAccountId,
      category: entryForm.category,
      subcategory: entryForm.subcategory.trim() || null,
      amount: Number(entryForm.amount),
      entryType: entryForm.entryType,
      description: entryForm.description.trim() || null,
      transactedDate: entryForm.transactedDate,
    });
    setSaving(false);
    if (result.success) {
      toast.success('Entry added');
      setEntryForm((f) => ({ ...f, amount: '', description: '', subcategory: '' }));
      fetchLedgerAccounts();
    } else toast.error(result.error || 'Failed');
  };

  const accountLabel = (id) => {
    const a = (ledgerAccounts || []).find((x) => String(x.id) === String(id));
    return a?.account_name || a?.accountName || '—';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Ledger</h1>
          <p className="text-sm text-gray-500">Accounts, categories, credit &amp; debit entries</p>
        </div>
        <div className="flex gap-4 text-sm">
          <div>
            <p className="text-[11px] uppercase text-gray-500 font-semibold">Opening (all)</p>
            <p className="font-bold tabular-nums text-gray-900">{rs(accountTotals.opening)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase text-gray-500 font-semibold">Closing (all)</p>
            <p className="font-bold tabular-nums text-emerald-700">{rs(accountTotals.closing)}</p>
          </div>
        </div>
      </div>

      {canManage && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <form onSubmit={onAccount} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-3">
            <h2 className="text-sm font-semibold text-gray-900">New account</h2>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Account name (Cash / UPI / Bank)"
              value={accountForm.accountName}
              onChange={(e) => setAccountForm((f) => ({ ...f, accountName: e.target.value }))}
            />
            <input
              type="number"
              step="0.01"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Opening balance"
              value={accountForm.openingBalance}
              onChange={(e) => setAccountForm((f) => ({ ...f, openingBalance: e.target.value }))}
            />
            <button type="submit" disabled={saving} className="bg-rose-800 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-rose-900 disabled:opacity-60">
              Add account
            </button>
          </form>

          <form onSubmit={onEntry} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-3">
            <h2 className="text-sm font-semibold text-gray-900">New entry</h2>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              value={entryForm.ledgerAccountId}
              onChange={(e) => setEntryForm((f) => ({ ...f, ledgerAccountId: e.target.value }))}
            >
              <option value="">Select account *</option>
              {(ledgerAccounts || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.account_name || a.accountName} (Bal: {rs(a.current_balance ?? a.currentBalance)})
                </option>
              ))}
            </select>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              value={entryForm.category}
              onChange={(e) => setEntryForm((f) => ({ ...f, category: e.target.value }))}
            >
              <option value="">Select category *</option>
              {(ledgerCategories || []).map((c) => (
                <option key={c.id} value={c.category_name}>{c.category_name}</option>
              ))}
            </select>
            {!(ledgerCategories || []).length && (
              <p className="text-xs text-amber-700">No categories yet — add them in Admin Settings → Ledger categories.</p>
            )}
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Subcategory (optional)"
              value={entryForm.subcategory}
              onChange={(e) => setEntryForm((f) => ({ ...f, subcategory: e.target.value }))}
            />
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setEntryForm((f) => ({ ...f, entryType: 'CREDIT' }))}
                className={`px-3 py-2.5 rounded-lg border-2 text-sm font-semibold ${
                  entryForm.entryType === 'CREDIT'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                Credit (Cr)
              </button>
              <button
                type="button"
                onClick={() => setEntryForm((f) => ({ ...f, entryType: 'DEBIT' }))}
                className={`px-3 py-2.5 rounded-lg border-2 text-sm font-semibold ${
                  entryForm.entryType === 'DEBIT'
                    ? 'border-rose-600 bg-rose-50 text-rose-800'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                Debit (Db)
              </button>
            </div>
            <p className="text-xs text-gray-500">Credit adds to balance · Debit reduces balance</p>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number"
                step="0.01"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Amount *"
                value={entryForm.amount}
                onChange={(e) => setEntryForm((f) => ({ ...f, amount: e.target.value }))}
              />
              <input
                type="date"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={entryForm.transactedDate}
                onChange={(e) => setEntryForm((f) => ({ ...f, transactedDate: e.target.value }))}
              />
            </div>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Description"
              value={entryForm.description}
              onChange={(e) => setEntryForm((f) => ({ ...f, description: e.target.value }))}
            />
            <button type="submit" disabled={saving} className="bg-stone-800 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-stone-900 disabled:opacity-60">
              Add entry
            </button>
          </form>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Accounts</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Account</th>
                <th className="px-3 py-2">Opening</th>
                <th className="px-3 py-2">Closing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(ledgerAccounts || []).map((a) => (
                <tr key={a.id}>
                  <td className="px-3 py-2 font-medium text-gray-900">{a.account_name || a.accountName}</td>
                  <td className="px-3 py-2 tabular-nums">{rs(a.opening_balance ?? a.openingBalance)}</td>
                  <td className="px-3 py-2 tabular-nums font-semibold text-emerald-700">
                    {rs(a.closing_balance ?? a.current_balance ?? a.currentBalance)}
                  </td>
                </tr>
              ))}
              {!(ledgerAccounts || []).length && (
                <tr><td colSpan={3} className="px-3 py-8 text-center text-gray-500">No accounts</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap gap-2 items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Entries</h2>
          <div className="flex flex-wrap gap-2">
            <select
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white"
              value={filters.accountId}
              onChange={(e) => setFilters((f) => ({ ...f, accountId: e.target.value }))}
            >
              <option value="">All accounts</option>
              {(ledgerAccounts || []).map((a) => (
                <option key={a.id} value={a.id}>{a.account_name || a.accountName}</option>
              ))}
            </select>
            <select
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white"
              value={filters.category}
              onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
            >
              <option value="">All categories</option>
              {(ledgerCategories || []).map((c) => (
                <option key={c.id} value={c.category_name}>{c.category_name}</option>
              ))}
            </select>
            <select
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white"
              value={filters.entryType}
              onChange={(e) => setFilters((f) => ({ ...f, entryType: e.target.value }))}
            >
              <option value="">Cr + Db</option>
              <option value="CREDIT">Credit</option>
              <option value="DEBIT">Debit</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[28rem]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 sticky top-0">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Account</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(ledgerEntries || []).map((e) => {
                const type = String(e.entry_type || e.entryType || '').toUpperCase();
                const isCredit = type === 'CREDIT';
                return (
                  <tr key={e.id}>
                    <td className="px-3 py-2 whitespace-nowrap">{String(e.transacted_date || '').slice(0, 10)}</td>
                    <td className="px-3 py-2">{accountLabel(e.ledger_account_id || e.ledgerAccountId)}</td>
                    <td className="px-3 py-2">
                      {e.category || '—'}
                      {e.subcategory ? <span className="text-xs text-gray-500"> · {e.subcategory}</span> : null}
                    </td>
                    <td className={`px-3 py-2 font-semibold ${isCredit ? 'text-emerald-700' : 'text-rose-800'}`}>
                      {isCredit ? 'Cr' : 'Db'}
                    </td>
                    <td className={`px-3 py-2 tabular-nums font-medium ${isCredit ? 'text-emerald-700' : 'text-rose-800'}`}>
                      {isCredit ? '+' : '-'}{rs(e.amount)}
                    </td>
                    <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{e.description || '—'}</td>
                  </tr>
                );
              })}
              {!(ledgerEntries || []).length && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No entries</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MuttonStallLedgerPage;
